import { useCallback } from 'react';
import { Briefcase } from 'lucide-react';
import { useJobStore } from '../../stores/jobStore';
import { usePolling } from '../../hooks/usePolling';
import { Spinner } from '../common/Spinner';
import { EmptyState } from '../common/EmptyState';
import JobStatsBar from './JobStatsBar';
import JobKanban from './JobKanban';
import JobDetailModal from './JobDetailModal';
import type { Job } from '../../types/jobs';

const POLL_INTERVAL = 30_000;

export default function JobsPage() {
  const jobs = useJobStore((s) => s.jobs);
  const stats = useJobStore((s) => s.stats);
  const recentRuns = useJobStore((s) => s.recentRuns);
  const loading = useJobStore((s) => s.loading);
  const selectedJob = useJobStore((s) => s.selectedJob);
  const setSelectedJob = useJobStore((s) => s.setSelectedJob);
  const fetchJobs = useJobStore((s) => s.fetchJobs);
  const fetchStats = useJobStore((s) => s.fetchStats);
  const fetchRecentRuns = useJobStore((s) => s.fetchRecentRuns);

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchJobs(), fetchStats(), fetchRecentRuns()]);
  }, [fetchJobs, fetchStats, fetchRecentRuns]);

  usePolling(fetchAll, POLL_INTERVAL);

  const handleJobClick = useCallback(
    (job: Job) => {
      setSelectedJob(job);
    },
    [setSelectedJob]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedJob(null);
  }, [setSelectedJob]);

  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!loading && jobs.length === 0) {
    return (
      <EmptyState
        icon={<Briefcase />}
        title="No Jobs Found"
        description="No scheduled jobs are configured yet."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <JobStatsBar stats={stats} />
      <JobKanban jobs={jobs} recentRuns={recentRuns} onJobClick={handleJobClick} />
      {selectedJob ? (
        <JobDetailModal job={selectedJob} onClose={handleCloseModal} />
      ) : null}
    </div>
  );
}
